'use client'

import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useLanguage } from '@/contexts/LanguageContext'
import { Phone, FileText, Leaf } from 'lucide-react'
import { useState } from 'react'

export default function HowItWorks() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const { language } = useLanguage()

  const [hoveredStep, setHoveredStep] = useState<number | null>(null)

  const steps = language === 'en' ? [
    {
      icon: Phone,
      title: "1. Free Consultation",
      description: "Contact me for a free, no-obligation consultation. I'll discuss your garden needs and schedule a visit.",
      details: "Phone, WhatsApp, or email - your choice!"
    },
    {
      icon: FileText,
      title: "2. Get Your Quote",
      description: "I'll provide a detailed, transparent quote within 24 hours. No hidden costs, no surprises.",
      details: "Clear pricing breakdown included"
    },
    {
      icon: Leaf,
      title: "3. Professional Service",
      description: "I'll transform your garden with care and attention to detail.",
      details: "Satisfaction guaranteed"
    }
  ] : [
    {
      icon: Phone,
      title: "1. Ymgynghoriad Am Ddim",
      description: "Cysylltwch â mi am ymgynghoriad am ddim, heb rwymedigaeth. Byddaf yn trafod eich anghenion gardd a threfnu ymweliad.",
      details: "Ffôn, WhatsApp, neu e-bost - eich dewis!"
    },
    {
      icon: FileText,
      title: "2. Cael Eich Dyfynbris",
      description: "Byddaf yn rhoi dyfynbris manwl, tryloyw o fewn 24 awr. Dim costau cudd, dim syndod.",
      details: "Dadansoddiad prisio clir wedi'i gynnwys"
    },
    {
      icon: Leaf,
      title: "3. Gwasanaeth Proffesiynol",
      description: "Byddaf yn trawsnewid eich gardd gyda gofal a sylw i fanylion.",
      details: "Bodlonrwydd wedi'i warantu"
    }
  ]

  return (
    <section id="how-it-works" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            {language === 'en' ? 'How It Works' : 'Sut Mae\'n Gweithio'}
          </h2>
          <p className="text-large text-gray-600 max-w-3xl mx-auto">
            {language === 'en' 
              ? 'Getting your garden transformed is simple. Here\'s my straightforward process:'
              : 'Mae cael eich gardd wedi\'i drawsnewid yn syml. Dyma fy mhroses uniongyrchol:'
            }
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => {
            const IconComponent = step.icon
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center group"
                onMouseEnter={() => setHoveredStep(index)}
                onMouseLeave={() => setHoveredStep(null)}
              >
                {/* Step Number */}
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-200 transition-colors">
                    <IconComponent className="w-8 h-8 text-gray-900" />
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gray-200 transform translate-x-4">
                      <div
                        className={`w-full h-full bg-gray-900 transition-transform duration-500 origin-left ${
                          hoveredStep !== null && hoveredStep >= index ? 'scale-x-100' : 'scale-x-0'
                        }`}
                      />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-sharp">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sharp">
                    {step.description}
                  </p>
                  <p className="text-sm text-gray-600 font-medium">
                    {step.details}
                  </p>
                </div>

                {/* Hover indicator */}
                <div className="absolute inset-0 bg-gray-50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
              </motion.div>
            )
          })}
        </div>
      </div>

      {/* CTA Section - now outside the container for full width */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="text-center mt-16"
      >
        <div className="w-full bg-gradient-to-br from-gray-900 to-gray-800 py-14 px-4 sm:px-8 md:px-16 lg:px-24 flex flex-col items-center justify-center shadow-xl">
          <h2 className="text-4xl font-bold text-white mb-4">
            {language === 'en' ? 'Ready to Get Started?' : 'Yn Barod i Ddechrau?'}
          </h2>
          <p className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl mx-auto font-medium">
            {language === 'en'
              ? 'Contact me today for your free consultation. No obligation, just professional advice.'
              : 'Cysylltwch â mi heddiw am eich ymgynghoriad am ddim. Dim rhwymedigaeth, dim ond cyngor proffesiynol.'
            }
          </p>
          <a
            href="#contact"
            className="inline-block px-8 py-4 bg-emerald-700 text-white font-semibold rounded-xl transition-all duration-300 hover:bg-emerald-800 hover:shadow-lg text-lg"
          >
            {language === 'en' ? 'Get Your Free Quote' : 'Cael Eich Dyfynbris Am Ddim'}
          </a>
        </div>
      </motion.div>
    </section>
  )
} 